import React, { useEffect, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { useProfile } from '@/context';
import ApiService from '../services/api'
import queryString from 'query-string';
import dayjs from 'dayjs';
import { NumberFormatBase, NumericFormat } from 'react-number-format';
import { formatRupiah } from '@/helpers/utils';

const SavingModal = ({ isOpen, onClose, onSubmit, title, subtitle, type, savingsTypeId }) => {
  // ALL Hooks must be called before any conditional returns
  const api = useApi();
  const params = queryString.parse(window.location.search);
  const id = params.id || null
  const { profile } = useProfile();
  const cooperativeId = profile?.cooperative?.id
  const [paymentList, setPaymentList] = useState([])
  const [bankList, setBankList] = useState([])
  const [schedule, setSchedule] = useState([])
  const [debitAccounts, setDebitAccounts] = useState([])
  const [creditAccounts, setCreditAccounts] = useState([])
  const [savingsType, setSavingsType] = useState(null)
  const [formData, setFormData] = useState({
    payment_method_id: '',
    amount: '',
    notes: '',
    payment_reference: '',
    payment_details: '',
    schedule_id: null,
    receipt_number: '',
    debit_account_id: '',
    credit_account_id: '',
  });

  // Define fetch functions before useEffect
  const fetchSchedule = async () => {
    const yearNow = dayjs().format('YYYY')
    if (id) {
      try {
        const schedule = await ApiService.getSchedule(id,type,yearNow);
        setSchedule(schedule?.data || [])
      } catch (error) {
        console.error("Failed to fetch payments:", error);
        setPaymentList([]);
      }
    }
  };

  const fetchPayments = async () => {
    if (cooperativeId) {
      try {
        const payments = await api.get('/cashier/payment-methods');
        
        if (payments && payments.data && typeof payments.data === 'object') {
          setPaymentList(payments.data);
        } else {
          setPaymentList([]);
        }
      } catch (error) {
        console.error("Failed to fetch payments:", error);
        setPaymentList([]);
      }
    }
  };

  const fetchSavingsType = async () => {
    if (savingsTypeId) {
      try {
        const response = await ApiService.getSavingsType(savingsTypeId);
        if (response && response.data) {
          setSavingsType(response.data);
          // Auto-populate COA accounts from savings type settings
          if (response.data.cash_account && response.data.savings_account) {
            setFormData(prev => ({
              ...prev,
              debit_account_id: response.data.cash_account,
              credit_account_id: response.data.savings_account,
            }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch savings type:", error);
      }
    }
  };

  const fetchChartOfAccounts = async () => {
    if (cooperativeId) {
      try {
        // Fetch debit accounts (ASSET - for deposit: debit kas/bank)
        const debitResponse = await ApiService.getChartOfAccountsByType(
          cooperativeId,
          'ASSET',
          'CURRENT_ASSET'
        );
        setDebitAccounts(debitResponse?.data || []);

        // Fetch credit accounts (EQUITY - for deposit: credit simpanan)
        const creditResponse = await ApiService.getChartOfAccountsByType(
          cooperativeId,
          'EQUITY',
          'MEMBER_EQUITY'
        );
        setCreditAccounts(creditResponse?.data || []);
      } catch (error) {
        console.error("Failed to fetch chart of accounts:", error);
        setDebitAccounts([]);
        setCreditAccounts([]);
      }
    }
  };

  // ALL useEffect hooks must be before early return
  useEffect(() => {
    if (isOpen && cooperativeId) {
      fetchSchedule()
      fetchPayments();
      fetchSavingsType();
      // Only fetch COA list if savings type doesn't have COA configured
      if (!savingsType || !savingsType.cash_account || !savingsType.savings_account) {
        fetchChartOfAccounts();
      }
    }
  }, [cooperativeId, isOpen, savingsTypeId]);

  useEffect(() => {
    if (!isOpen) return;
    
    switch (formData.payment_method_id) {
      case '42':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"cash\", \"received_by\": \"Kasir Koperasi\"}"
        }));
        break;
      case '43':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"bank_transfer\", \"bank_name\": \"Bank BCA\", \"account_number\": \"1234567890\", \"transfer_date\": \"2025-08-11\"}"
        }));
        break;
      case '44':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"debit_card\", \"card_number\": \"**1234\", \"bank_name\": \"Bank Mandiri\"}"
        }));
        break;
      case '45':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"QRIS\", \"received_by\": \"Kasir Koperasi\"}"
        }));
        break;
      default:
        break;
    }
  },[formData.payment_method_id, isOpen])

  // Early return AFTER all hooks
  if (!isOpen) return null;

  const getStatusBadge = (status) => {
    const badges = {
      waiting_for_confirmation: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      overdue: 'bg-red-100 text-red-800 border-red-300',
      pending: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return badges[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getLabelStatusBadge = (status) => {
    const badges = {
      waiting_for_confirmation: 'Diproses',
      paid: 'Dibayar',
      overdue: 'Telat',
      pending: 'Belum Dibayar'
    };
    return badges[status] || 'Belum Dibayar';
  }

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: files ? files[0] : value, 
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Modal Container */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative w-full max-w-lg max-h-full bg-white rounded-lg shadow-lg">
          
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h5 className="text-xl font-semibold text-gray-800">
              {title}
            </h5>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-900"
              onClick={onClose}
            >
              <i className='mgc_close_line'></i>
            </button>
          </div>

          {formData.schedule_id === null && type !== 'voluntary' && (
            <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {schedule.length > 0 ?
                schedule?.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center border rounded-md gap-4 p-4 shadow-sm"
                    >
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="text-sm font-semibold">
                          {subtitle} {dayjs(item.due_date).format("MMMM")}
                        </h4>
                        <span
                          className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadge(
                            item.status
                          )}`}
                        >
                          {getLabelStatusBadge(item.status)}
                        </span>
                      </div>

                      <div className="text-sm text-gray-700 space-y-1">
                        <p>
                          Minimal Pembayaran:{" "}
                          <span className="font-medium">
                            {formatRupiah(item?.scheduled_amount) || "-"}
                          </span>
                        </p>
                        <p>
                          Tenggat Waktu:{" "}
                          <span className="font-medium">
                            {dayjs(item.due_date).format("DD/MM/YYYY")}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <button
                        className="py-1 px-3 rounded-md text-green-500 border border-green-500 hover:bg-green-500 hover:text-white disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-sm font-medium"
                        onClick={() => setFormData({ schedule_id: item.id })}
                        disabled={
                          item.status === "waiting_for_confirmation" || item.status === "paid"
                        }
                      >
                        Bayar
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className='flex justify-between items-center border rounded-md gap-3 p-3'>
                    <div>
                      <div className='flex items-center'>
                        <h4>Tidak ada data</h4>
                      </div>
                    </div>
                  </div>
                )
              }
            </div>
          )}

          {(formData.schedule_id !== null || type === 'voluntary') && (
            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <h4>Produk Simpanan</h4>
                  <p>{ subtitle }</p>
                </div>
                {/* Payments ID */}
                <div>
                  <label htmlFor="payment_method_id" className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                      id="payment_method_id"
                      name="payment_method_id"
                      value={formData.payment_method_id}
                      onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Payment</option>
                    {paymentList?.map((res) => (
                      <option key={res.id} value={res.id}>
                        {res.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* COA Information or Manual Selection */}
                {savingsType && savingsType.cash_account && savingsType.savings_account ? (
                  // Show COA info if configured in savings settings
                  <div className="col-span-2 bg-green-50 border border-green-200 rounded-md p-4">
                    <h5 className="text-sm font-semibold text-green-800 mb-2">
                      <i className="mgc_check_circle_line mr-1"></i>
                      Akun Jurnal (Auto dari Pengaturan Simpanan)
                    </h5>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Akun Debit (Kas/Bank):</span>
                        <p className="font-medium text-gray-900">{savingsType.cash_account}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Akun Kredit (Simpanan):</span>
                        <p className="font-medium text-gray-900">{savingsType.savings_account}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Show manual selection if COA not configured
                  <>
                    {/* Debit Account */}
                    <div>
                      <label htmlFor="debit_account_id" className="block text-sm font-medium text-gray-700">
                        Akun Debit (Kas/Bank) <span className="text-red-500">*</span>
                      </label>
                      <select
                          id="debit_account_id"
                          name="debit_account_id"
                          value={formData.debit_account_id}
                          onChange={handleChange}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                      >
                        <option value="">Pilih Akun Debit</option>
                        {debitAccounts?.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.account_code} - {account.account_name}
                          </option>
                        ))}
                      </select>
                      <small className="text-yellow-600 text-xs mt-1 block">
                        <i className="mgc_alert_line mr-1"></i>
                        COA belum di-set di pengaturan simpanan. Silakan pilih manual.
                      </small>
                    </div>

                    {/* Credit Account */}
                    <div>
                      <label htmlFor="credit_account_id" className="block text-sm font-medium text-gray-700">
                        Akun Kredit (Simpanan) <span className="text-red-500">*</span>
                      </label>
                      <select
                          id="credit_account_id"
                          name="credit_account_id"
                          value={formData.credit_account_id}
                          onChange={handleChange}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                      >
                        <option value="">Pilih Akun Kredit</option>
                        {creditAccounts?.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.account_code} - {account.account_name}
                          </option>
                        ))}
                      </select>
                      <small className="text-yellow-600 text-xs mt-1 block">
                        <i className="mgc_alert_line mr-1"></i>
                        COA belum di-set di pengaturan simpanan. Silakan pilih manual.
                      </small>
                    </div>
                  </>
                )}

                {/* Amount */}
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
                  <NumericFormat
                    name="amount"
                    id="amount"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="Rp "
                    allowNegative={false}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Masukkan nominal"
                    value={formData.amount}
                    onValueChange={(values) => {
                      setFormData({
                        ...formData,
                        amount: values.value, 
                      });
                    }}
                  />
                </div>
              </div>

              {/* Modal Footer (Form Actions) */}
              <div className="flex justify-end p-4 border-t border-gray-200">
                <button
                  type="button"
                  className="btn mr-2"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Submit Payment
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default SavingModal;